 
/**
 * S20 · Sign-off — canvas-based signature pad. On submit we serialize
 * the canvas to a PNG blob and hand it to `submitSession` which
 * writes it to OPFS + enqueues both the blob upload and the
 * `session.submit` task.
 */
import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Btn,
  Chip,
  GestureBar,
  I,
  Ico,
  StatusBar,
  T,
} from '@/components/primitives';
import { useSession, useSubmitSessionMutation } from '@kapture/client-core';

export const Route = createFileRoute('/sessions/$sessionId/review/sign')({
  component: SignOffPage,
  staticData: { mobile: true },
});

function SignOffPage() {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const { data: session } = useSession(sessionId);
  const submit = useSubmitSessionMutation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    // One-time setup: match the canvas bitmap to its CSS size for crisp
    // strokes on hi-DPI screens.
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = T.ink;
    ctx.lineWidth = 2.2;
  }, []);

  const drawing = useRef(false);

  function pointerLocal(e: React.PointerEvent): { x: number; y: number } | null {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function onDown(e: React.PointerEvent) {
    const p = pointerLocal(e);
    const ctx = canvasRef.current?.getContext('2d');
    if (!p || !ctx) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    drawing.current = true;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  }

  function onMove(e: React.PointerEvent) {
    if (!drawing.current) return;
    const p = pointerLocal(e);
    const ctx = canvasRef.current?.getContext('2d');
    if (!p || !ctx) return;
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!drawn) setDrawn(true);
  }

  function onUp() {
    drawing.current = false;
  }

  function clear() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setDrawn(false);
  }

  async function finalize() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/png'),
    );
    if (!blob) return;
    const { session: submitted } = await submit.mutateAsync({
      sessionId,
      signature: blob,
      signatureByteSize: blob.size,
      signerName: 'Sarah Chen',
      attestation: 'I certify this inspection is true to my direct observation.',
    });
    navigate({
      to: '/sessions/$sessionId/review/success',
      params: { sessionId: submitted.id },
    });
  }

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: T.surface,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: T.font,
      }}
    >
      <StatusBar />
      <div
        style={{
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: `1px solid ${T.stroke}`,
        }}
      >
        <button
          type="button"
          onClick={() => navigate({ to: '/sessions/$sessionId/review', params: { sessionId } })}
          aria-label="Back"
          style={{ background: 'transparent', border: 'none', padding: 0, color: T.text, cursor: 'pointer', display: 'flex' }}
        >
          <Ico d={I.back} size={20} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, fontFamily: T.font }}>Sign & submit</div>
          <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.font }}>
            {session?.title ?? '—'}
          </div>
        </div>
        <Chip tone="good" size="sm">
          Verified
        </Chip>
      </div>

      <div style={{ padding: 16 }}>
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 12,
            background: T.surface2,
            border: `1px solid ${T.stroke}`,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              background: T.brand,
              color: T.brandInk,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontFamily: T.font,
              fontSize: 15,
            }}
          >
            SC
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: T.font }}>Sarah Chen</div>
            <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.mono }}>
              Cert #IE-4412 · Field inspector
            </div>
          </div>
        </div>
        <div
          style={{
            marginTop: 12,
            fontSize: 13,
            color: T.textDim,
            fontFamily: T.font,
            lineHeight: 1.55,
            fontStyle: 'italic',
          }}
        >
          &quot;I certify this inspection is true to my direct observation on-site.&quot;
        </div>
      </div>

      <div style={{ padding: '0 16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            fontSize: 10,
            color: T.textMute,
            fontFamily: T.mono,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: 6,
          }}
        >
          Signature
        </div>
        <div
          style={{
            flex: 1,
            borderRadius: 14,
            border: `1.5px dashed ${T.stroke2}`,
            background: T.surface,
            position: 'relative',
            minHeight: 200,
            touchAction: 'none',
          }}
        >
          <canvas
            ref={canvasRef}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              cursor: 'crosshair',
            }}
          />
          {!drawn && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
                color: T.textMute,
                fontSize: 13,
                fontFamily: T.font,
                pointerEvents: 'none',
              }}
            >
              Sign with your finger or stylus
            </div>
          )}
          <button
            type="button"
            onClick={clear}
            aria-label="Clear signature"
            style={{
              position: 'absolute',
              bottom: 10,
              right: 10,
              padding: '4px 10px',
              borderRadius: 100,
              background: T.surface,
              border: `1px solid ${T.stroke}`,
              color: T.textDim,
              fontFamily: T.font,
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        </div>
        <div
          style={{
            marginTop: 8,
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          <Chip tone="good" size="sm" icon={<Ico d={I.check2} size={11} />}>
            Timestamp captured
          </Chip>
          <Chip tone="good" size="sm" icon={<Ico d={I.pin} size={11} />}>
            Location locked
          </Chip>
        </div>
      </div>

      <div
        style={{
          padding: '10px 16px 12px',
          borderTop: `1px solid ${T.stroke}`,
          display: 'flex',
          gap: 8,
        }}
      >
        <Btn
          tone="light"
          size="md"
          onClick={() =>
            navigate({ to: '/sessions/$sessionId/review', params: { sessionId } })
          }
        >
          Back
        </Btn>
        <Btn
          tone="primary"
          size="md"
          full
          iconRight={<Ico d={I.check2} size={16} sw={2.4} />}
          disabled={!drawn || submit.isPending}
          onClick={() => void finalize()}
          style={{ opacity: !drawn || submit.isPending ? 0.5 : 1 }}
        >
          {submit.isPending ? 'Submitting…' : 'Confirm & submit'}
        </Btn>
      </div>
      <GestureBar />
    </div>
  );
}
